import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchSloCollectionCurrent,
  fetchSloSilverStagingCurrent,
  fetchSloIngestCurrent,
} from '../../api/client';
import {
  Section,
  MetricsLegend,
  TenantIndicator,
  formatLatency,
  formatRelativeMinutes,
} from './sloShared';

const POLL_INTERVAL_MS = 30000;

// Collection (Stage 1) emits PascalCase workload names; stages 2 & 3 use
// snake_case. Map Stage 1 → canonical snake_case so we can join across stages.
const COLLECTION_TO_CANONICAL = {
  AADSignIn: 'signin',
  GraphSignIn: 'graph_signin',
  ExchangeAudit: 'exchange',
  SharePointAudit: 'sharepoint',
  GeneralAudit: 'general',
  DLPAudit: 'dlp',
  DirectoryAudits: 'directory_audit',
  MessageTrace: 'message_trace',
  UrlClickEvents: 'url_click',
  RiskDetections: 'risk_detection',
  RiskyUsers: 'risky_user',
  SecurityAlerts: 'security_alert',
  SecurityIncidents: 'security_incident',
  AzureActivityLog: 'azure_activity',
  DeviceSecurityEvents: 'device_security',
  DeviceProcessEvents: 'device_process_events',
  GraphSignInNonInteractive: 'graph_signin_noninteractive',
};

// Case-insensitive view of the map above. The collection emitter lives in a
// different service, so its exact PascalCase can drift (GraphSignInNonInteractive
// vs GraphSigninNoninteractive); matching on the lowercased name keeps the
// cross-stage join from silently splitting one workload into two partial rows.
const COLLECTION_TO_CANONICAL_LC = Object.fromEntries(
  Object.entries(COLLECTION_TO_CANONICAL).map(([k, v]) => [k.toLowerCase(), v]),
);

// Workloads that exist ONLY as a Stage-2 (bronze→silver) ETL sub-unit — they have
// no counterpart label at collection (Stage 1) or unified-Delta ingest (Stage 3)
// because the data is collected/ingested under its parent log_type (e.g. exchange).
// For these, the missing stages are "not applicable" rather than "no data yet".
const STAGE2_ONLY_WORKLOADS = new Set([
  'exchange_bulk',
]);

// Workloads to omit even if they appear (defensive — backend should already exclude them).
const EXCLUDED_WORKLOADS = new Set([
  'drmm_site_devices',
  'bpc_agent_devices',
  'entra_registered_devices',
  'intune_managed_devices',
]);

const LEGEND = [
  { term: 'End-to-end', defn: 'Time from when an event occurred at the M365 source to when it has been appended to our unified silver Delta table. Includes Microsoft-side indexing delay.' },
  { term: 'Microsoft + cadence', defn: 'eventToPollSec — time spent in Microsoft\'s indexing pipeline plus our deliberate poll cadence (currently 30 min). Out of our control. Activity Feed sources can have very long values here when Microsoft re-indexes old events.' },
  { term: 'Our processing', defn: 'pollToUploadSec (Stage 1) + Stage 2 latency_sec + Stage 3 latency_seconds — the total time spent inside our pipeline code. This is what we control and what the SLO alerts fire on.' },
  { term: 'Stage 1 (Collection)', defn: 'M365 source → bronze-staging. Average eventToUpload (Microsoft + cadence + our processing combined for this stage).' },
  { term: 'Stage 2 (Bronze → Silver)', defn: 'bronze-staging → silver-staging. Pure ETL transform time.' },
  { term: 'Stage 3 (Silver → Delta)', defn: 'silver-staging → unified Delta append. Auto Loader + Delta write time.' },
  { term: 'avg', defn: 'Mean. Sum of latencies divided by count of samples in the last hour. Big number in each cell.' },
  { term: 'p50 (median)', defn: 'Half of records are at or below this latency. Helps spot skew: if avg << p50, the average is being dragged down by a lot of fast records; if avg >> p50, a few outliers are dragging it up.' },
  { term: 'n=N', defn: 'Sample count in the last hour. If n is small (< 10) for any stage, the avg/p50 for that stage is statistically thin — treat it with skepticism.' },
];

export default function SummaryTab({ active, tenantId = '', tenantLookup = {} }) {
  const [collection, setCollection] = useState(null);
  const [silver, setSilver] = useState(null);
  const [ingest, setIngest] = useState(null);
  const [displayedTenantId, setDisplayedTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [c, s, i] = await Promise.allSettled([
        fetchSloCollectionCurrent({ tenantId }),
        fetchSloSilverStagingCurrent({ tenantId }),
        fetchSloIngestCurrent({ tenantId }),
      ]);
      if (c.status === 'fulfilled') setCollection(c.value);
      if (s.status === 'fulfilled') setSilver(s.value);
      if (i.status === 'fulfilled') setIngest(i.value);
      const errs = [c, s, i].filter((r) => r.status === 'rejected').map((r) => r.reason?.message || String(r.reason));
      if (errs.length) {
        setError(`Some stages failed to load: ${errs.join('; ')}`);
      } else {
        setError('');
      }
      setDisplayedTenantId(tenantId);
    } catch (e) {
      setError(e?.message || 'Failed to load summary data.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (!active) return undefined;
    load(false);
    const id = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active, load]);

  // Merge by canonical workload name. Per row we keep both the MS-delay piece
  // (Stage 1's eventToPollAvgSec) and the "ours" pieces (Stage 1 pollToUpload
  // + Stage 2 + Stage 3) so the table can render the math explicitly.
  const merged = useMemo(() => {
    const map = new Map();
    const upsert = (canonical, key, row) => {
      if (!canonical || EXCLUDED_WORKLOADS.has(canonical)) return;
      if (!map.has(canonical)) map.set(canonical, { collection: null, silver: null, ingest: null });
      map.get(canonical)[key] = row;
    };

    for (const w of (collection?.workloads || [])) {
      const lc = (w.workload || '').toLowerCase();
      const canonical = COLLECTION_TO_CANONICAL_LC[lc] || lc;
      upsert(canonical, 'collection', w);
    }
    for (const w of (silver?.workloads || [])) {
      upsert(w.workload, 'silver', w);
    }
    for (const w of (ingest?.workloads || [])) {
      if (w.status === 'scheduled') continue;
      upsert(w.workload, 'ingest', w);
    }

    const rows = [];
    for (const [canonical, entry] of map.entries()) {
      const c = entry.collection || {};
      const s = entry.silver || {};
      const i = entry.ingest || {};

      // Stage 1 (Collection) — has v2 split between Microsoft+cadence and ours
      const microsoftCadenceAvgSec = entry.collection ? Number(c.eventToPollAvgSec ?? 0) : null;
      const microsoftCadenceP50Sec = entry.collection ? Number(c.eventToPollP50Sec ?? 0) : null;
      const stage1OursAvgSec       = entry.collection ? Number(c.avgSec ?? 0) : null;
      const stage1OursP50Sec       = entry.collection ? Number(c.p50Sec ?? 0) : null;
      const stage1Samples          = entry.collection ? Number(c.samples ?? 0) : null;
      const stage1TotalAvgSec      = entry.collection ? Number(c.eventToUploadAvgSec ?? ((microsoftCadenceAvgSec || 0) + (stage1OursAvgSec || 0))) : null;

      // Stage 2 (Bronze → Silver)
      const stage2AvgSec  = entry.silver ? Number(s.avgSec ?? 0) : null;
      const stage2P50Sec  = entry.silver ? Number(s.p50Sec ?? 0) : null;
      const stage2Samples = entry.silver ? Number(s.samples ?? 0) : null;

      // Stage 3 (Silver → Delta)
      const hasIngest = entry.ingest && i.samples > 0;
      const stage3AvgSec  = hasIngest ? Number(i.avgSec ?? 0) : null;
      const stage3P50Sec  = hasIngest ? Number(i.p50Sec ?? 0) : null;
      const stage3Samples = entry.ingest ? Number(i.samples ?? 0) : null;

      const ourTotalSec   = (stage1OursAvgSec || 0) + (stage2AvgSec || 0) + (stage3AvgSec || 0);
      const grandTotalSec = (microsoftCadenceAvgSec || 0) + ourTotalSec;
      const stagesPresent = [stage1TotalAvgSec, stage2AvgSec, stage3AvgSec].filter((x) => x != null && x > 0).length;

      // Lowest sample count across the populated stages — drives the "thin data" warning
      const sampleCounts = [stage1Samples, stage2Samples, stage3Samples].filter((x) => x != null);
      const minSamples = sampleCounts.length > 0 ? Math.min(...sampleCounts) : 0;

      rows.push({
        workload: canonical,
        stage2Only: STAGE2_ONLY_WORKLOADS.has(canonical),
        microsoftCadenceAvgSec,
        microsoftCadenceP50Sec,
        stage1OursAvgSec,
        stage1OursP50Sec,
        stage1Samples,
        stage2AvgSec,
        stage2P50Sec,
        stage2Samples,
        stage3AvgSec,
        stage3P50Sec,
        stage3Samples,
        ourTotalSec,
        grandTotalSec,
        stagesPresent,
        minSamples,
      });
    }
    return rows.sort((a, b) => b.grandTotalSec - a.grandTotalSec);
  }, [collection, silver, ingest]);

  // Headline aggregation: average across workloads with data in all 3 stages.
  // Track Microsoft-cadence and our-processing separately so the headline can show the breakdown.
  const headline = useMemo(() => {
    const usable = merged.filter((r) => r.stagesPresent === 3);
    const pool = usable.length > 0 ? usable : merged.filter((r) => r.grandTotalSec > 0);
    if (pool.length === 0) return null;

    const sumMs    = pool.reduce((a, r) => a + (r.microsoftCadenceAvgSec || 0), 0);
    const sumOurs  = pool.reduce((a, r) => a + r.ourTotalSec, 0);
    const sumTotal = pool.reduce((a, r) => a + r.grandTotalSec, 0);

    // Total raw sample counts that fed the average (sums of per-workload stage samples).
    const sumS1 = pool.reduce((a, r) => a + (r.stage1Samples || 0), 0);
    const sumS2 = pool.reduce((a, r) => a + (r.stage2Samples || 0), 0);
    const sumS3 = pool.reduce((a, r) => a + (r.stage3Samples || 0), 0);

    return {
      microsoftCadenceAvgSec: sumMs / pool.length,
      ourAvgSec: sumOurs / pool.length,
      totalAvgSec: sumTotal / pool.length,
      partial: usable.length === 0,
      count: pool.length,
      stage1Samples: sumS1,
      stage2Samples: sumS2,
      stage3Samples: sumS3,
    };
  }, [merged]);

  const generatedUtc = collection?.generatedUtc || silver?.generatedUtc || ingest?.generatedUtc;

  return (
    <div>
      {error && (
        <div className="card mb-4 bg-amber-900/20 border-amber-700/40 text-amber-200 text-sm">
          {error}
        </div>
      )}

      <Section
        title="Average end-to-end pipeline time (Microsoft delay + our pipeline)"
        subtitle={
          <>
            Average time from when a record was emitted at the M365 source to when it landed in our unified silver Delta table. <span className="text-gray-400">Includes Microsoft-side indexing delay (out of our control)</span> + our 3-stage processing time. Last 1 hour. Daily-snapshot workloads excluded.
          </>
        }
        right={
          <div className="flex items-center gap-3">
            <TenantIndicator
              selectedTenantId={tenantId}
              displayedTenantId={displayedTenantId}
              tenantLookup={tenantLookup}
              loading={loading}
            />
            {generatedUtc && (
              <div className="text-xs text-gray-500">
                {loading ? 'refreshing…' : `updated ${formatRelativeMinutes(generatedUtc)}`}
              </div>
            )}
            <MetricsLegend items={LEGEND} />
          </div>
        }
      >
        <HeadlineBlock headline={headline} loading={loading && !collection && !silver && !ingest} />
      </Section>

      <Section
        title="Per-workload breakdown"
        subtitle="How an average record's wait time decomposes — Microsoft-side delay vs each of our processing stages. Sorted by total."
      >
        <PerWorkloadTable rows={merged} loading={loading && merged.length === 0} />
      </Section>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// HeadlineBlock — large equation: MS+cadence + our processing = total.
// ───────────────────────────────────────────────────────────────────────────
function HeadlineBlock({ headline, loading }) {
  if (loading) {
    return <div className="text-sm text-gray-500 italic px-3 py-6">Loading…</div>;
  }
  if (!headline) {
    return (
      <div className="text-sm text-gray-500 italic px-3 py-6">
        No pipeline data in the last hour. The dashboard will populate as soon as the silver pipeline emits its next batch.
      </div>
    );
  }
  const totalSamples = (headline.stage1Samples || 0) + (headline.stage2Samples || 0) + (headline.stage3Samples || 0);
  return (
    <div>
      <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 py-2">
        <div className="text-center">
          <div className="text-3xl font-semibold text-gray-300 tabular-nums">{formatLatency(headline.microsoftCadenceAvgSec)}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Microsoft + cadence</div>
        </div>
        <span className="text-3xl text-gray-500 font-light">+</span>
        <div className="text-center">
          <div className="text-3xl font-semibold text-blue-300 tabular-nums">{formatLatency(headline.ourAvgSec)}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Our processing (Stages 1–3)</div>
        </div>
        <span className="text-3xl text-gray-500 font-light">=</span>
        <div className="text-center">
          <div className="text-5xl font-bold text-gray-100 tabular-nums">{formatLatency(headline.totalAvgSec)}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">End-to-end (Microsoft + ours)</div>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        {headline.partial
          ? `Partial: averaged across ${headline.count} workload${headline.count === 1 ? '' : 's'} present in some stages — full-pipeline data hasn't landed for every workload yet.`
          : `Averaged across ${headline.count} workload${headline.count === 1 ? '' : 's'} that have data in all 3 stages.`}
        <span className="text-gray-600">
          {' · sample counts feeding this number: Stage 1 n='}<span className="text-gray-400 tabular-nums">{headline.stage1Samples}</span>
          {', Stage 2 n='}<span className="text-gray-400 tabular-nums">{headline.stage2Samples}</span>
          {', Stage 3 n='}<span className="text-gray-400 tabular-nums">{headline.stage3Samples}</span>
          {` (${totalSamples} total).`}
        </span>
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// PerWorkloadTable — explicit math row: MS + S1ours + S2 + S3 = Total.
// Each stage cell shows avg (large) + p50 + sample count (smaller).
// ───────────────────────────────────────────────────────────────────────────
const THIN_DATA_THRESHOLD = 10;  // n below this = "thin data" warning

function PerWorkloadTable({ rows, loading }) {
  if (loading) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">Loading…</div>;
  }
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">No workload data yet.</div>;
  }

  const opCellClass = 'px-1 py-2 text-gray-500 text-center font-light align-top pt-3';
  const valCellWrapClass = 'px-2 py-2 align-top';

  const labelCell = (text, sub) => (
    <div className="text-right leading-tight">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{text}</div>
      {sub && <div className="text-[9px] text-gray-600 normal-case tracking-normal">{sub}</div>}
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-800">
            <th className="px-3 py-2 font-medium text-gray-400 text-left align-bottom">Workload</th>
            <th className="px-2 py-2 align-bottom">{labelCell('Microsoft', '+ cadence')}</th>
            <th className={opCellClass}>+</th>
            <th className="px-2 py-2 align-bottom">{labelCell('Stage 1', 'our collection')}</th>
            <th className={opCellClass}>+</th>
            <th className="px-2 py-2 align-bottom">{labelCell('Stage 2', 'bronze→silver')}</th>
            <th className={opCellClass}>+</th>
            <th className="px-2 py-2 align-bottom">{labelCell('Stage 3', 'silver→delta')}</th>
            <th className={opCellClass}>=</th>
            <th className="px-2 py-2 align-bottom">{labelCell('Total', 'end-to-end')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.workload} className="border-b border-gray-800/50 hover:bg-gray-900/40">
              <td className="px-3 py-2 font-mono text-xs text-gray-300 align-top pt-3">{r.workload}</td>
              <td className={valCellWrapClass}>
                <StageCell avgSec={r.microsoftCadenceAvgSec} p50Sec={r.microsoftCadenceP50Sec} samples={r.stage1Samples} tone="gray" notApplicable={r.stage2Only} />
              </td>
              <td className={opCellClass}>+</td>
              <td className={valCellWrapClass}>
                <StageCell avgSec={r.stage1OursAvgSec} p50Sec={r.stage1OursP50Sec} samples={r.stage1Samples} tone="blue" notApplicable={r.stage2Only} />
              </td>
              <td className={opCellClass}>+</td>
              <td className={valCellWrapClass}>
                <StageCell avgSec={r.stage2AvgSec} p50Sec={r.stage2P50Sec} samples={r.stage2Samples} tone="blue" />
              </td>
              <td className={opCellClass}>+</td>
              <td className={valCellWrapClass}>
                <StageCell avgSec={r.stage3AvgSec} p50Sec={r.stage3P50Sec} samples={r.stage3Samples} tone="blue" notApplicable={r.stage2Only} />
              </td>
              <td className={opCellClass}>=</td>
              <td className="px-2 py-2 text-right tabular-nums align-top pt-3">
                <div className="font-semibold text-gray-100">
                  {r.grandTotalSec > 0 ? formatLatency(r.grandTotalSec) : '—'}
                  {r.stagesPresent < 3 && r.grandTotalSec > 0 && (
                    <span className="ml-1 text-[10px] text-amber-500" title="Total is partial — not all 3 stages reported data">*</span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-xs text-gray-500 mt-3 px-3 flex items-center gap-4 flex-wrap">
        <span><span className="inline-block w-3 h-3 align-middle bg-gray-500/60 rounded-sm mr-1.5" />gray = Microsoft-side delay (out of our control)</span>
        <span><span className="inline-block w-3 h-3 align-middle bg-blue-500/60 rounded-sm mr-1.5" />blue = our pipeline processing</span>
        <span className="text-amber-400"><span className="mr-1">⚠</span>amber n=N = thin data (&lt; {THIN_DATA_THRESHOLD} samples in the last hour — avg/p50 may not be representative)</span>
        <span className="text-gray-500"><span className="italic mr-1">n/a</span>= stage not applicable — workload exists only at the bronze→silver stage.</span>
        <span className="text-gray-600"><span className="text-amber-500 mr-1">*</span>partial total — fewer than 3 stages reported data for this workload.</span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// StageCell — vertical stack: avg (top, larger) / p50 (middle) / n=N (bottom).
// `tone` controls the avg color so Microsoft (gray) is visually different
// from our pipeline stages (blue).
// ───────────────────────────────────────────────────────────────────────────
function StageCell({ avgSec, p50Sec, samples, tone = 'blue', notApplicable = false }) {
  if (avgSec == null && samples == null) {
    return (
      <div
        className="text-right text-gray-600 italic"
        title={notApplicable ? 'Not applicable — this workload only exists at the bronze→silver stage' : undefined}
      >
        {notApplicable ? 'n/a' : '—'}
      </div>
    );
  }
  const noSamples = samples != null && samples === 0;
  const thin = samples != null && samples > 0 && samples < THIN_DATA_THRESHOLD;
  const avgClass = tone === 'gray' ? 'text-gray-400' : 'text-blue-300';
  const sampleClass = thin
    ? 'text-amber-400'
    : (noSamples ? 'text-gray-600' : 'text-gray-500');

  return (
    <div className="text-right leading-tight">
      <div className={`tabular-nums ${avgClass}`}>
        {avgSec == null ? <span className="text-gray-600 italic">—</span> : formatLatency(avgSec)}
      </div>
      <div className="text-[10px] text-gray-500 tabular-nums mt-0.5">
        p50 {p50Sec == null ? '—' : formatLatency(p50Sec)}
      </div>
      <div className={`text-[10px] tabular-nums ${sampleClass}`}>
        {thin && <span className="mr-0.5">⚠</span>}
        n={samples == null ? '—' : samples}
      </div>
    </div>
  );
}
